import React, { useContext } from "react";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_ABOUT_PRODUCT,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { SopHubEntryId } from "./enums";
import { SOP_HUB_CATEGORIES } from "./constants/sopHubContent";
import {
  publicInfoBodySx,
  publicInfoFeatureCardSx,
  publicInfoFeatureIconSx,
  publicInfoLinkSx,
  publicInfoSectionTitleSx
} from "./constants/publicInfoPageStyles";

const SOP_ENTRY_ICONS: Record<string, React.ElementType> = {
  [SopHubEntryId.FORGOT_PASSWORD]: LockResetOutlinedIcon,
  [SopHubEntryId.MANAGE_PROFILE]: PersonOutlineIcon,
  [SopHubEntryId.DASHBOARD]: SpaceDashboardOutlinedIcon,
  [SopHubEntryId.MANAGE_STORES]: StorefrontOutlinedIcon,
  [SopHubEntryId.MANAGE_COMPANY]: BusinessOutlinedIcon,
  [SopHubEntryId.ADD_CATEGORY]: CategoryOutlinedIcon,
  [SopHubEntryId.ADD_PRODUCT]: Inventory2OutlinedIcon,
  [SopHubEntryId.ADD_PRODUCT_ITEM]: QrCode2OutlinedIcon,
  [SopHubEntryId.CREATE_ORDER]: AddShoppingCartOutlinedIcon,
  [SopHubEntryId.MANAGE_ORDER]: EditNoteOutlinedIcon,
  [SopHubEntryId.ISSUE_INVOICE]: ReceiptLongOutlinedIcon,
  [SopHubEntryId.RECORD_PAYMENT]: PaymentsOutlinedIcon
};

const SopHubPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const pathFor = (route: string) =>
    language != null && defaultLang != null
      ? localizedPath(route, language, defaultLang)
      : `/${route}`;

  const aboutProductPath = pathFor(PAGE_ROUTE_ABOUT_PRODUCT);
  const userGuidePath = pathFor(PAGE_ROUTE_USER_GUIDE);
  const contactPath = pathFor(PAGE_ROUTE_CONTACT);

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="sops-hub-page"
        eyebrow={t("sops-hub-page-eyebrow", "Task-by-task help")}
        title={t("sops-hub-page-title", "Run books")}
        intro={t(
          "sops-hub-page-intro",
          "Focused walkthroughs with screenshots for common tasks in Kivunova FMS. Pick a guide below and follow each step in order."
        )}
      />

      <PublicInfoCallout testId="sops-hub-page-note">
        {t(
          "sops-hub-page-note",
          "These guides cover one workflow at a time. For a broader setup overview, see the User guide."
        )}
      </PublicInfoCallout>

      <Stack spacing={3.5} sx={{ mt: 3 }}>
        {SOP_HUB_CATEGORIES.map((category) => (
          <Box
            key={category.id}
            component="section"
            data-testid={`sops-hub-category-${category.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleSx}>
              {t(category.titleKey, category.titleFallback)}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)"
                },
                gap: { xs: 1.5, sm: 2 },
                mt: 1.5
              }}
            >
              {category.entries.map((entry) => {
                const Icon =
                  SOP_ENTRY_ICONS[entry.id] ?? Inventory2OutlinedIcon;
                const entryPath = pathFor(entry.route);

                return (
                  <MuiLink
                    key={entry.id}
                    component={Link}
                    to={entryPath}
                    underline="none"
                    color="inherit"
                    data-testid={`sops-hub-entry-${entry.id}`}
                    sx={{
                      display: "block",
                      height: "100%",
                      "&:hover .sops-hub-entry-arrow": {
                        transform: "translateX(4px)"
                      }
                    }}
                  >
                    <Box sx={publicInfoFeatureCardSx}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1.5
                        }}
                      >
                        <Box sx={publicInfoFeatureIconSx}>
                          <Icon fontSize="small" />
                        </Box>
                        <ArrowForwardIcon
                          className="sops-hub-entry-arrow"
                          fontSize="small"
                          sx={{
                            color: "primary.main",
                            mt: 0.5,
                            flexShrink: 0,
                            transition: "transform 0.2s ease"
                          }}
                        />
                      </Box>
                      <Typography
                        component="h3"
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: "primary.main",
                          mb: 0.75,
                          letterSpacing: "-0.01em"
                        }}
                      >
                        {t(entry.titleKey, entry.titleFallback)}
                      </Typography>
                      <Typography variant="body2" sx={publicInfoBodySx}>
                        {t(entry.descriptionKey, entry.descriptionFallback)}
                      </Typography>
                    </Box>
                  </MuiLink>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t("sops-hub-page-cta-title", "Need more context?")}
        testId="sops-hub-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "sops-hub-page-cta-body",
            "Understand how products and stock lines work together, or read the full setup guide."
          )}{" "}
          <MuiLink
            component={Link}
            to={aboutProductPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "sops-hub-page-cta-about-product-link",
              "Products vs. product items"
            )}
          </MuiLink>
          {t("sops-hub-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("sops-hub-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("sops-hub-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("sops-hub-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default SopHubPage;
